import 'core-js/stable';
import Events from '../lib/events';
import Device from '../lib/device';
import Host from '../lib/host';
import { environment } from '../lib/util';
import GoogleAnalyticsTracker from './trackers/googleanalytics';

export default class BasePlayer {
  // receives an element selector and a setup object
  constructor(sel, setup) {
    environment.env = setup.env;
    this.el = document.querySelector(sel);
    this.type = setup.type;
    this.video = setup.video;
    this.asset = setup.asset;
    this.options = setup.params;

    this.source = setup.params.source;
    this.watchedSeconds = 0;
    this.trackers = {};

    const asset = this.asset;
    if (asset.enables_google_analytics && this.allowPersonalizedCookies()) {
      this.trackers.googleAnalytics = new GoogleAnalyticsTracker(this);
    }

    this.marks = {};

    // Resolve all autoplay configurations, supports:
    // ?autoplay=[1,true,sound,paused]
    // ?autoplay_with_sound=[1,true] (deprecated)
    // Note that autoplay=paused is a special deferred configuration
    // that enables "playsinline" for scrolling, but does not automatically start the video.
    this.autoplayAudio = /sound/.test(setup.params.autoplay) || /1|true/.test(setup.params.autoplay_with_sound);
    this.autoplayVideo = /1|true/.test(setup.params.autoplay) || this.autoplayAudio;
    this.playsInline = /paused/.test(setup.params.autoplay) || this.autoplayVideo || Device.isAndroid;
    this.autoplayMuted = (this.autoplayVideo && !this.autoplayAudio);

    // Add window error handling
    window.addEventListener('error', (message, filename, lineno, colno, error) => {
      this.track({ action: Events.ERROR, message: `Window error ${message} ${filename} ${error}` });
    });

    // Configure cross-frame messenger client
    this.host = new Host(setup)
      .on(Events.VIEW, () => this.markAsViewed())
      .on(Events.PLAY, () => this.play())
      .on(Events.PAUSE, () => this.pause())
      .connect();
  }

  allowPersonalizedCookies() {
    switch (this.options.privacy_consent) {
    case 'essential':
      return false;

    case 'all':
    default:
      return true;
    }
  }

  load() {
    // load player setup. override in subclasses.
  }

  /**
  * Starts video playback.
  */
  play() {
    this.player && this.player.play();
  }

  /**
  * Pauses video playback.
  */
  pause() {
    this.player && this.player.pause();
  }

  /**
  * Destroys the player, releasing for garbage collection.
  */
  destroy() {
    this.player && this.player.dispose();
  }

  /**
  * Unmutes the player.
  */
  unmute() {
    this.player && this.player.muted(false);
  }

  // Removes any trace of `?autoplay=sfoo` from the URL,
  // so that videojs wont see it and automatically autoplay
  // (not sure if this is videojs itself, or a Brightcove distro of it).
  // The `playing` replacement is just dummy text for videojs to ignore.
  // Call this method *before* building a videojs player.
  clearUrlAutoplay() {
    const autoplayPattern = /(^|\?|&)(autoplay)(=|$)/g;
    if (autoplayPattern.test(location.search)) {
      const sanitized = location.search.replace(autoplayPattern, '$1playing$3');
      history.replaceState({}, document.title, location.pathname + sanitized);
    }
  }

  // Display a thumbnail when not autoplaying.
  // For VideoJS players (chorus, brightcove).
  // @todo make this exclusive to ChorusPlayer once brightcove is removed.
  setAutoplay(posterUrl) {
    const defaultPosterUrl = 'https://volume-assets.voxmedia.com/static-images/default-thumb.jpg';

    if (this.autoplayVideo) {
      this.player.controls(true);

      if (this.autoplayMuted) {
        this.player.muted(true);
      }

      if (Device.isMobile || Device.isTablet) {
        this.player.poster(posterUrl || defaultPosterUrl);
      } else {
        this.player.autoplay(true);
      }

      this.player.play();

    } else {
      this.player.addClass('vjs-autoplay-disabled');
      this.player.poster(posterUrl || defaultPosterUrl);
    }
  }

  // Toggles an un-mute button over the top of the player.
  // For VideoJS players only (chorus, brightcove).
  // @todo make this exclusive to ChorusPlayer once brightcove is removed.
  toggleUnmuteButton() {
    if (!this.autoplayMuted || !this.player) return;
    const muted = this.player.muted();

    if (muted && !this.muteButton) {
      const btn = document.createElement('button');
      btn.className = 'volume-unmute';
      btn.innerHTML = '<svg class="unmute-icon" xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" viewBox="0 0 18.2 24.4" style="enable-background:new 0 0 18.2 24.4;" xml:space="preserve"><title>Unmute Audio</title><path d="M15.1,0.7c0-0.4-0.4-0.8-0.8-0.7c-0.2,0-0.4,0.1-0.5,0.2L8.3,5.7H4.8C3.2,5.7,1.9,7,1.9,8.5v7.4c0,0.2,0,0.4,0.1,0.6 L15.1,3.4L15.1,0.7L15.1,0.7z"/><path d="M18,3.9c-0.3-0.3-0.8-0.3-1.1,0l-1.8,1.8l0,0L2.8,18l-2.5,2.5c-0.3,0.3-0.4,0.8-0.1,1.1C0.5,21.9,1,22,1.3,21.7 c0,0,0,0,0.1-0.1l8.2-8.2l5.4-5.4l0,0L18,5C18.3,4.7,18.3,4.2,18,3.9z"/><path d="M8.2,18.8l5.5,5.5c0.3,0.3,0.8,0.3,1.1,0c0.1-0.1,0.2-0.3,0.2-0.5l0.1-13.5l-8.6,8.6L8.2,18.8L8.2,18.8z"/></svg> <span>Tap to Unmute</span>';
      btn.addEventListener('click', evt => {
        evt.preventDefault();
        btn.remove();
        this.unmute();
        this.muteButton = null;
      });

      this.muteButton = btn;
      document.body.appendChild(btn);
    } else if (!muted && this.muteButton) {
      this.muteButton.remove();
      this.muteButton = null;
    }
  }

  /**
  * Focuses media attention on this video client.
  * All other video clients are paused.
  * Requires the embed API to be loaded onto the host page.
  * External chorus page will respond to "pauseOthers"
  * by sending a pause message to all other players.
  */
  pauseOtherPlayers() {
    this.host.post('pauseOthers');
  }

  /**
  * Tracks a singleton view event.
  * This operates as a singleton that only allows one view to be reported.
  */
  markAsViewed() {
    if (!this.viewed) {
      this.viewed = true;
      this.track({ action: Events.VIEW });
    }
  }

  /**
  * Sends the event to all trackers
  */
  track(analyticsEvent) {
    analyticsEvent = analyticsEvent || {};
    if (!analyticsEvent.action) return;

    // Proxy event to all registered trackers.
    Object.keys(this.trackers).forEach(key => this.trackers[key].track(analyticsEvent));
  }

  /**
  * Starts a 1-second interval to count the seconds that a video has been watched.
  */
  timeStart() {
    this.timeInterval = setInterval(() => {
      this.watchedSeconds += 1;
      this.el.dispatchEvent(new Event('playTimeUpdate'));
    }, 1000);
  }

  timeStop() {
    clearInterval(this.timeInterval);
  }

  /**
  * Sets a performance benchmark.
  * @param {String} label of the mark to set.
  */
  mark(label) {
    if (!this.marks[label]) {
      this.marks[label] = Date.now();
    }
  }

  /**
  * Tracks time since a previously set mark.
  * @param {String} label of the mark to measure.
  * @param {String} label for reporting to analytics.
  */
  trackTimeSinceMark(action, label) {
    if (this.marks[label]) {
      var duration = Math.floor(Date.now() - this.marks[label]);
      // Cap response duration to prevent outliers from skewing data:
      if (duration < 100000) this.track({ action: action, value: duration });
    }
  }
}
